import OneSignal from "react-onesignal";
import { fetchNotifications } from "src/stores/notification";
import { store } from "../stores";
import { addReminderToast } from "src/stores/reminderToasts/slice";
import sound from "./notification-sound.mp3";
// import { setAlert } from "src/stores/basicSlices";
import { fetchTicketQueues } from "src/stores/ticketQueue";
import { addCallLogsToast } from "src/stores/callLogsToasts/slice";
import {
  setHasDeviceToken,
  setPermissionStatus,
} from "../stores/notificationPermission";
import { fetchQueues } from "src/stores/queue";
import {
  MW_CALL_PUSH_TYPE,
  readCallPushFromActiveNotifications,
  takePendingCallPush,
} from "./pendingCallPush";

export default async function runOneSignal() {
  try {
    const stored = localStorage.getItem("deviceToken");
    const appId: any =
      process.env.REACT_APP_ONESIGNAL_APP_ID ||
      "f7d70ef2-7f06-4efe-a1f5-ad9b77ce7870";

    // Always init OneSignal first
    await OneSignal.init({
      appId,
      allowLocalhostAsSecureOrigin: true,
    });

    // Listen for permission granted event from modal
    const handlePermissionGranted = async () => {
      // Re-run initialization when permission is granted
      setTimeout(async () => {
        await runOneSignal();
      }, 1000);
    };

    // Remove existing listener to avoid duplicates
    window.removeEventListener(
      "notification-permission-granted",
      handlePermissionGranted,
    );
    window.addEventListener(
      "notification-permission-granted",
      handlePermissionGranted,
    );

    // Determine permission and request if needed
    let permission: NotificationPermission = Notification.permission;

    if (permission !== "granted" && permission !== "denied") {
      try {
        // Prefer OneSignal API (falls back to Notification API internally)
        // @ts-ignore - typings can differ between SDK versions
        const result: any = await (
          OneSignal as any
        ).Notifications?.requestPermission?.();
        if (typeof result === "string") {
          permission = result as NotificationPermission;
        } else {
          // Fallback to standard API
          permission = await Notification.requestPermission();
        }
      } catch {
        permission = await Notification.requestPermission();
      }
    }

    if (permission === "denied") {
      localStorage.removeItem("deviceToken");
      store.dispatch(setHasDeviceToken(false));
      store.dispatch(setPermissionStatus("denied"));
      return null;
    }

    if (permission === "granted") {
      try {
        // Ensure we are subscribed to push
        // @ts-ignore - SDK v16 exposes subscribe()
        if ((OneSignal as any).User?.PushSubscription?.optedIn === false) {
          await (OneSignal as any).User?.PushSubscription?.subscribe?.();
        }
      } catch {}

      // Try to get a stable identifier: PushSubscription id, then OneSignal user id
      // @ts-ignore
      let token: string | null | undefined = (OneSignal as any).User
        ?.PushSubscription?.id;
      // console.log("token000>", token);
      try {
        if (!token && (OneSignal as any).User?.getId) {
          token = await (OneSignal as any).User.getId();
        }
      } catch {}

      if (token && `${token}`.trim()) {
        localStorage.setItem("deviceToken", `${token}`);
        store.dispatch(setHasDeviceToken(true));
        store.dispatch(setPermissionStatus("granted"));
        return token as string;
      }

      // Fallback to previously stored value if available
      if (stored && `${stored}`.trim()) {
        store.dispatch(setHasDeviceToken(true));
        store.dispatch(setPermissionStatus("granted"));
        return stored;
      }

      store.dispatch(setHasDeviceToken(false));
      return null;
    }

    // If permission is default (neither granted nor denied), return stored value if any
    store.dispatch(setPermissionStatus("default"));
    if (stored && `${stored}`.trim()) {
      store.dispatch(setHasDeviceToken(true));
      return stored;
    }

    store.dispatch(setHasDeviceToken(false));
    return stored;
  } catch (error) {
    console.log(error);
    return null;
  }
}

export const usePushNotification = () => {
  async function requestPermission() {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      let workerRegistered = false;

      for (const registration of registrations) {
        if (
          registration.active &&
          registration.active.scriptURL.includes("OneSignalSDKWorker.js")
        ) {
          workerRegistered = true;
          break;
        }
      }
      const permission = await Notification.requestPermission();
      if (permission === "granted") {
        if (!workerRegistered) {
          if ("serviceWorker" in navigator) {
            const registration = await navigator.serviceWorker.register(
              "/OneSignalSDKWorker.js",
            );
            console.log("Service Worker Registered");
          }
        }
      }
    } catch (error) {
      console.error(
        "Error during permission request or service worker registration:",
        error,
      );
    }
  }

  requestPermission();

  const genToastId = (prefix: string) => {
    try {
      if (typeof crypto !== "undefined" && (crypto as any).randomUUID) {
        return (crypto as any).randomUUID();
      }
    } catch (e) {}
    return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  };

  /** Normalize OneSignal foreground/click payloads into one shape */
  const getNotificationAdditionalData = (event: any) =>
    event?.notification?.additionalData ||
    event?.additionalData ||
    event?.data ||
    {};

  /**
   * Shared handler for call push events.
   * foregroundWillDisplay only fires while the CRM tab is focused.
   * click fires when the user opens a background OS notification —
   * without this, disconnect can arrive as a notification but never
   * update Redux, leaving the floating popup stuck on "Incoming Call".
   */
  const handlePushEvent = (event: any, options?: { playSound?: boolean }) => {
    const isLoggedIn = store?.getState().authUser?.token;
    if (!isLoggedIn) return;

    if (options?.playSound) {
      try {
        new Audio(sound).play();
      } catch (e) {}
    }

    const additionalData = getNotificationAdditionalData(event);
    const notification = event?.notification || event;
    const type = additionalData?.eventType;
    const notificationType = additionalData?.notification_type;

    // Keep meta.raw.notification.additionalData so CallsLogsModal can read it
    const raw = event?.notification
      ? event
      : {
          notification: {
            ...notification,
            additionalData,
          },
        };

    if (notificationType === "call_queue_reminder") {
      store?.dispatch(
        addCallLogsToast({
          id: genToastId("reminder"),
          title: notification?.title || "Call Feedback Reminder",
          description:
            notification?.body || "You have pending call feedback",
          createdAt: Date.now(),
          meta: { raw },
        } as any),
      );
      return;
    }

    if (type === "call_inbound" || type === "call_outbound") {
      // Same Setup/Answered/Disconnected can arrive from SW + foreground/click
      const sessionId =
        additionalData?.sessionId || additionalData?.session_id || null;
      const code = String(additionalData?.code || "").toLowerCase();
      const existing = store?.getState()?.callLogsToasts || [];
      const alreadyQueued = existing.some((t: any) => {
        const ad =
          t?.meta?.raw?.notification?.additionalData ||
          t?.meta?.raw?.additionalData;
        if (!ad) return false;
        const existingSession = ad.sessionId || ad.session_id;
        return (
          sessionId &&
          existingSession === sessionId &&
          String(ad.code || "").toLowerCase() === code
        );
      });
      if (alreadyQueued) return;

      store?.dispatch(
        addCallLogsToast({
          id: genToastId("calllogs"),
          title: notification?.title,
          description: notification?.body || notification?.description,
          createdAt: Date.now(),
          meta: { raw },
        } as any),
      );
      return;
    }

    store?.dispatch(fetchNotifications());
    store?.dispatch(fetchTicketQueues({ page: 1, limit: 10, reset: true }));
    store?.dispatch(fetchQueues({}));

    try {
      store?.dispatch(
        addReminderToast({
          id: genToastId("reminder"),
          title: notification?.title,
          description: notification?.body,
          createdAt: Date.now(),
          meta: { raw },
        } as any),
      );
    } catch (err) {}
  };

  OneSignal.Notifications.addEventListener(
    "foregroundWillDisplay",
    (event: any) => handlePushEvent(event, { playSound: true }),
  );
  OneSignal.Notifications.addEventListener("click", (event: any) =>
    handlePushEvent(event, { playSound: false }),
  );

  /**
   * Background-tab recovery (does not replace foreground/click handlers):
   * 1) SW postMessage when a call push is bridged
   * 2) IndexedDB snapshot from the SW
   * 3) Still-visible OS notifications via getNotifications()
   *    (works even when OneSignal encrypted the raw push — matches the
   *    case where Chrome still shows "Incoming Call Ringing")
   */
  const applyWorkerCallPush = (message: any, playSound = false) => {
    if (!message || message.type !== MW_CALL_PUSH_TYPE) return;
    handlePushEvent(message, { playSound });
  };

  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.addEventListener("message", (event) => {
      applyWorkerCallPush(event?.data, true);
    });
  }

  const recoverBackgroundCallPush = async () => {
    if (document.visibilityState !== "visible") return;

    const pending = await takePendingCallPush();
    if (pending) applyWorkerCallPush(pending, true);

    const fromOsNotification = await readCallPushFromActiveNotifications();
    if (fromOsNotification) applyWorkerCallPush(fromOsNotification, true);
  };

  document.addEventListener("visibilitychange", () => {
    void recoverBackgroundCallPush();
  });
  window.addEventListener("focus", () => {
    void recoverBackgroundCallPush();
  });

  void recoverBackgroundCallPush();
};
